import UnitPageShell from '../components/shared/UnitPageShell';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import authService from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { apiConfig } from '../utils/apiConfig';
import { UserProfile, parseScore } from '../types/user';
// PageHeader intentionally omitted (not used here)

type AdminUser = UserProfile & { active?: boolean; deleted?: boolean };

type TeamOption = {
  id: number;
  name: string;
  is_active?: number | boolean;
};

const isTeamActive = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true';
  return false;
};

type MessageState = {
  severity: 'success' | 'error' | 'info';
  text: string;
};

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'light_admin', label: 'Light Admin' },
  { value: 'user_admin', label: 'User Admin' },
  { value: 'team_admin', label: 'Team Admin' },
  { value: 'role_admin', label: 'Role Admin' },
  { value: 'updates_admin', label: 'Updates Admin' },
  { value: 'quiz_admin', label: 'Quiz Admin' },
  { value: 'docs_admin', label: 'Docs Admin' },
  { value: 'phonebook_admin', label: 'Phonebook Admin' },
  { value: 'faq_admin', label: 'FAQ Admin' },
  { value: 'dashboard_admin', label: 'Dashboard Admin' },
  { value: 'highscore_admin', label: 'Highscore Admin' },
  { value: 'content_admin', label: 'Content Admin' },
  { value: 'editor_1', label: 'Editor L1' },
  { value: 'editor_2', label: 'Editor L2' },
  { value: 'editor_3', label: 'Editor L3' },
  { value: 'editor_4', label: 'Editor L4' },
  { value: 'editor_5', label: 'Editor L5' },
  { value: 'editor_6', label: 'Editor L6' },
  { value: 'editor_7', label: 'Editor L7' },
  { value: 'editor_8', label: 'Editor L8' },
  { value: 'editor_9', label: 'Editor L9' },
  { value: 'editor_10', label: 'Editor L10' },
  { value: 'creator_1', label: 'Creator L1' },
  { value: 'creator_2', label: 'Creator L2' },
  { value: 'creator_3', label: 'Creator L3' },
  { value: 'creator_4', label: 'Creator L4' },
  { value: 'creator_5', label: 'Creator L5' },
  { value: 'creator_6', label: 'Creator L6' },
  { value: 'creator_7', label: 'Creator L7' },
  { value: 'creator_8', label: 'Creator L8' },
  { value: 'creator_9', label: 'Creator L9' },
  { value: 'creator_10', label: 'Creator L10' },
  { value: 'aussendienst', label: 'Aussendienst' },
  { value: 'inbetriebnehmer', label: 'Inbetriebnehmer' },
  { value: 'programmierer', label: 'Programmierer' },
  { value: 'cad', label: 'CAD' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'support', label: 'Support' },
  { value: 'qa', label: 'QA' },
  { value: 'user', label: 'User' }
];

const normalizeRoles = (roles: unknown): string[] => {
  if (Array.isArray(roles)) {
    return roles
      .map((r) => String(r).trim())
      .filter((r) => r.length > 0);
  }
  if (typeof roles === 'string') {
    return roles
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.length > 0);
  }
  return [];
};

const roleLabel = (roleValue: string): string => {
  const match = ROLE_OPTIONS.find((opt) => opt.value === roleValue);
  return match ? match.label : roleValue;
};

const AdminPage: React.FC = () => {
  const { token, canManageUsers, canManageTeams, canManageUpdates, canViewRolesLegend, canAccessAdmin } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<MessageState | null>(null);
  const [edits, setEdits] = useState<Record<string, Partial<AdminUser>>>({});
  const [busyUsers, setBusyUsers] = useState<Record<string, boolean>>({});
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<string | false>(false);
  const [waDialogUser, setWaDialogUser] = useState<AdminUser | null>(null);
  const [waMessage, setWaMessage] = useState('');
  const [waSending, setWaSending] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserAlias, setNewUserAlias] = useState('');
  const [newUserRoles, setNewUserRoles] = useState<string[]>(['user']);
  const [newUserActive, setNewUserActive] = useState(false);
  const [newUserEvent, setNewUserEvent] = useState('');
  const [newUserWeeklyGoalNotifyEnabled, setNewUserWeeklyGoalNotifyEnabled] = useState(true);
  const [creatingUser, setCreatingUser] = useState(false);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [teamEdits, setTeamEdits] = useState<Record<number, string>>({});
  const [teamActiveEdits, setTeamActiveEdits] = useState<Record<number, boolean>>({});
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsSaving, setTeamsSaving] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [rolePointGoals, setRolePointGoals] = useState<Record<string, number>>({});
  const [rolePointGoalEdits, setRolePointGoalEdits] = useState<Record<string, string>>({});
  const [roleGoalsLoading, setRoleGoalsLoading] = useState(false);
  const [roleGoalsSaving, setRoleGoalsSaving] = useState(false);
  const [runningWeeklyRoleGoals, setRunningWeeklyRoleGoals] = useState(false);

  const canAccessPage = canAccessAdmin;

  const loadUsers = useCallback(async () => {
    if (!token || !canManageUsers) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await authService.getAllUsers(token);
      if (!Array.isArray(payload)) {
        throw new Error('Unerwartetes Format von der Benutzerliste.');
      }
      setUsers(payload);
    } catch (e: any) {
      setError(e?.message || 'Beim Laden der Benutzerliste ist ein Fehler aufgetreten.');
    } finally {
      setLoading(false);
    }
  }, [token, canManageUsers]);

  const loadTeams = useCallback(async () => {
    if (!token) return;
    setTeamsLoading(true);
    try {
      const res = await fetch(`${apiConfig.apiUrl}?action=getTeams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && Array.isArray(data.teams)) {
        setTeams(data.teams);
        const mapped: Record<number, string> = {};
        const activeMapped: Record<number, boolean> = {};
        data.teams.forEach((t: TeamOption) => {
          mapped[t.id] = t.name;
          activeMapped[t.id] = isTeamActive(t.is_active);
        });
        setTeamEdits(mapped);
        setTeamActiveEdits(activeMapped);
      }
    } catch {
      // ignore
    } finally {
      setTeamsLoading(false);
    }
  }, [token]);

  const loadRolePointGoals = useCallback(async () => {
    if (!token || !canManageUsers) return;
    setRoleGoalsLoading(true);
    try {
      const result = await authService.getRolePointGoals(token);
      if (result?.success === false) {
        return;
      }
      const rawMap = result?.roleMinPoints && typeof result.roleMinPoints === 'object'
        ? result.roleMinPoints
        : {};
      const normalized: Record<string, number> = {};
      Object.entries(rawMap).forEach(([roleKey, value]) => {
        const points = Number(value);
        if (!Number.isFinite(points) || points < 0) return;
        normalized[roleKey] = Math.round(points);
      });
      setRolePointGoals(normalized);
      const mappedEdits: Record<string, string> = {};
      Object.entries(normalized).forEach(([roleKey, points]) => {
        mappedEdits[roleKey] = String(points);
      });
      setRolePointGoalEdits(mappedEdits);
    } catch {
      // ignore load errors in this section
    } finally {
      setRoleGoalsLoading(false);
    }
  }, [token, canManageUsers]);

  useEffect(() => {
    if (!canManageUsers) return;
    loadUsers();
  }, [loadUsers, refreshTrigger, canManageUsers]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    loadRolePointGoals();
  }, [loadRolePointGoals]);

  useEffect(() => {
    if (users.length === 0) return;
    const mapped: Record<string, Partial<AdminUser>> = {};
    users.forEach(user => {
      mapped[user.id] = {
        email: user.email ?? '',
        alias: user.alias ?? '',
        roles: normalizeRoles(user.roles ?? user.role ?? 'user'),
        event: user.event ?? '',
        info: user.info ?? '',
        team_id: user.team_id ?? null,
        weekly_goal_notify_enabled: user.weekly_goal_notify_enabled ?? true
      };
    });
    setEdits(mapped);
  }, [users]);

  const availableGoalRoles = useMemo(() => {
    const roleSet = new Set<string>();
    ROLE_OPTIONS.forEach((option) => roleSet.add(option.value));
    users.forEach((user) => {
      normalizeRoles(user.roles ?? user.role ?? 'user').forEach((roleValue) => roleSet.add(roleValue));
    });
    Object.keys(rolePointGoals).forEach((roleValue) => roleSet.add(roleValue));

    return Array.from(roleSet).sort((a, b) => roleLabel(a).localeCompare(roleLabel(b), 'de'));
  }, [users, rolePointGoals]);

  const handleAccordionChange = (panel: string) => (_e: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const setUserBusy = (userId: string, busy: boolean) => {
    setBusyUsers(prev => ({ ...prev, [userId]: busy }));
  };

  const handleFieldChange = (userId: string, field: keyof AdminUser, value: string | string[] | number | null | boolean) => {
    setEdits(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value
      }
    }));
  };

  const handleSave = async (user: AdminUser) => {
    if (!token) return;
    const edit = edits[user.id];
    if (!edit) return;
    
    const changes: Record<string, any> = {};
    
    const editAlias = String(edit.alias ?? '').trim();
    const userAlias = String(user.alias ?? '').trim();
    if (editAlias && editAlias !== userAlias) {
      changes.alias = editAlias;
    }

    const editEmail = String(edit.email ?? '').trim();
    const userEmail = String(user.email ?? '').trim();
    if (editEmail && editEmail !== userEmail) {
      changes.email = editEmail;
    }
    
    const editRoles = normalizeRoles(edit.roles ?? user.roles ?? user.role ?? 'user');
    const userRoles = normalizeRoles(user.roles ?? user.role ?? 'user');
    const editRolesKey = [...editRoles].sort().join(',');
    const userRolesKey = [...userRoles].sort().join(',');
    if (editRolesKey !== userRolesKey) {
      changes.roles = editRoles.join(',');
    }
    
    const editEvent = String(edit.event ?? '').trim();
    const userEvent = String(user.event ?? '').trim();
    if (editEvent !== userEvent) {
      changes.event = editEvent || null;
    }
    
    const editInfo = String(edit.info ?? '');
    const userInfo = String(user.info ?? '');
    if (editInfo !== userInfo) {
      changes.info = editInfo;
    }

    const editWeeklyGoalNotify = Boolean(edit.weekly_goal_notify_enabled ?? true);
    const userWeeklyGoalNotify = Boolean(user.weekly_goal_notify_enabled ?? true);
    if (editWeeklyGoalNotify !== userWeeklyGoalNotify) {
      changes.weeklyGoalNotifyEnabled = editWeeklyGoalNotify;
    }

    const editTeamRaw = edit.team_id ?? null;
    const userTeamRaw = user.team_id ?? null;
    const editTeamStr = editTeamRaw === null || editTeamRaw === undefined ? '' : String(editTeamRaw);
    const userTeamStr = userTeamRaw === null || userTeamRaw === undefined ? '' : String(userTeamRaw);
    const editTeamId = editTeamStr === '' ? null : Number(editTeamStr);
    const userTeamId = userTeamStr === '' ? null : Number(userTeamStr);
    if (editTeamId !== userTeamId) {
      changes.teamId = editTeamId ?? '';
    }
    
    if (Object.keys(changes).length === 0) {
      setMessage({ severity: 'info', text: 'Keine Änderungen für diesen Benutzer gefunden.' });
      return;
    }
    
    if (!changes.alias && editAlias === '') {
      setMessage({ severity: 'error', text: 'Alias darf nicht leer sein.' });
      return;
    }

    if (!changes.email && editEmail === '') {
      setMessage({ severity: 'error', text: 'E-Mail darf nicht leer sein.' });
      return;
    }
    
    setMessage(null);
    setUserBusy(user.id, true);
    try {
      const result = await authService.adminUpdateUserFieldsGet(token, { userId: user.id, ...changes });
      if (result.success) {
        setMessage({
          severity: 'success',
          text: `Benutzer ${user.email} wurde aktualisiert.`
        });
        setRefreshTrigger(prev => prev + 1);
      } else {
        setMessage({ severity: 'error', text: result.message || 'Fehler beim Speichern.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Fehler beim Speichern.' });
    } finally {
      setUserBusy(user.id, false);
    }
  };

  const handleSaveTeams = async () => {
    if (!token) return;
    setTeamsSaving(true);
    setMessage(null);
    try {
      const payload = {
        action: 'updateTeams',
        teams: teams.map((team) => ({
          id: team.id,
          name: (teamEdits[team.id] ?? team.name).trim(),
          is_active: teamActiveEdits[team.id] ?? true
        }))
      };
      const res = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && Array.isArray(data.teams)) {
        setTeams(data.teams);
        const mapped: Record<number, string> = {};
        const activeMapped: Record<number, boolean> = {};
        data.teams.forEach((t: TeamOption) => {
          mapped[t.id] = t.name;
          activeMapped[t.id] = isTeamActive(t.is_active);
        });
        setTeamEdits(mapped);
        setTeamActiveEdits(activeMapped);
        setMessage({ severity: 'success', text: 'Teams wurden gespeichert.' });
      } else {
        setMessage({ severity: 'error', text: data?.error || 'Teams konnten nicht gespeichert werden.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Teams konnten nicht gespeichert werden.' });
    } finally {
      setTeamsSaving(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!token) return;
    const name = newTeamName.trim();
    if (!name) {
      setMessage({ severity: 'error', text: 'Teamname darf nicht leer sein.' });
      return;
    }
    setCreatingTeam(true);
    setMessage(null);
    try {
      const res = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'createTeam', name })
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success && Array.isArray(data.teams)) {
        setTeams(data.teams);
        const mapped: Record<number, string> = {};
        const activeMapped: Record<number, boolean> = {};
        data.teams.forEach((t: TeamOption) => {
          mapped[t.id] = t.name;
          activeMapped[t.id] = isTeamActive(t.is_active);
        });
        setTeamEdits(mapped);
        setTeamActiveEdits(activeMapped);
        setNewTeamName('');
        setMessage({ severity: 'success', text: 'Team wurde angelegt.' });
      } else {
        setMessage({ severity: 'error', text: data?.error || 'Team konnte nicht angelegt werden.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Team konnte nicht angelegt werden.' });
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleRoleGoalInputChange = (roleValue: string, rawValue: string) => {
    setRolePointGoalEdits((prev) => ({
      ...prev,
      [roleValue]: rawValue
    }));
  };

  const handleSaveRolePointGoals = async () => {
    if (!token) return;
    setRoleGoalsSaving(true);
    setMessage(null);
    try {
      const payload: Record<string, number> = {};
      Object.entries(rolePointGoalEdits).forEach(([roleValue, rawValue]) => {
        const trimmed = String(rawValue ?? '').trim();
        if (trimmed === '') return;
        const points = Number(trimmed);
        if (!Number.isFinite(points) || points < 0) return;
        payload[roleValue] = Math.round(points);
      });

      const result = await authService.updateRolePointGoals(token, payload);
      if (result?.success === false) {
        setMessage({ severity: 'error', text: result.error || 'Rollen-Ziele konnten nicht gespeichert werden.' });
        return;
      }

      const savedMap = result?.roleMinPoints && typeof result.roleMinPoints === 'object'
        ? result.roleMinPoints
        : payload;
      const normalized: Record<string, number> = {};
      Object.entries(savedMap).forEach(([roleValue, value]) => {
        const points = Number(value);
        if (!Number.isFinite(points) || points < 0) return;
        normalized[roleValue] = Math.round(points);
      });

      setRolePointGoals(normalized);
      const mappedEdits: Record<string, string> = {};
      Object.entries(normalized).forEach(([roleValue, points]) => {
        mappedEdits[roleValue] = String(points);
      });
      setRolePointGoalEdits(mappedEdits);
      setMessage({ severity: 'success', text: 'Rollen-Ziele wurden gespeichert.' });
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Rollen-Ziele konnten nicht gespeichert werden.' });
    } finally {
      setRoleGoalsSaving(false);
    }
  };

  const handleRunWeeklyRoleGoalNotifications = async () => {
    if (!token) return;
    setRunningWeeklyRoleGoals(true);
    setMessage(null);
    try {
      const result = await authService.triggerWeeklyRoleGoalNotifications(token, true);
      if (result?.success === false) {
        setMessage({ severity: 'error', text: result.error || 'Wochenziel-Erinnerungen konnten nicht gesendet werden.' });
        return;
      }
      const sent = Number(result?.sent ?? 0);
      const eligible = Number(result?.eligible ?? 0);
      setMessage({
        severity: 'success',
        text: `Wochenziel-Erinnerungen ausgeführt: ${sent} gesendet${eligible > 0 ? ` (betroffen: ${eligible})` : ''}.`
      });
      setRefreshTrigger(prev => prev + 1);
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Wochenziel-Erinnerungen konnten nicht gesendet werden.' });
    } finally {
      setRunningWeeklyRoleGoals(false);
    }
  };

  const handleToggleActive = async (user: AdminUser) => {
    if (!token) return;
    const targetState = user.active ? 0 : 1;
    setMessage(null);
    setUserBusy(user.id, true);
    try {
      // Nutze denselben Endpoint wie Android (robust, serverseitig erprobt)
      const result = await authService.updateUserActiveStatus(token, user.email, targetState === 1);
      if (result.success) {
        setMessage({
          severity: 'success',
          text: `${user.email} wurde ${targetState === 1 ? 'aktiviert' : 'deaktiviert'}.`
        });
        setRefreshTrigger(prev => prev + 1);
      } else {
        setMessage({ severity: 'error', text: result.message || 'Status konnte nicht geändert werden.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Status konnte nicht geändert werden.' });
    } finally {
      setUserBusy(user.id, false);
    }
  };

  const handleDelete = async (user: AdminUser) => {
    if (!token) return;
    if (!window.confirm(`Benutzer ${user.email} wirklich löschen?`)) return;
    setMessage(null);
    setDeletingUserId(user.id);
    try {
      const result = await authService.deleteUserByIdGet(token, user.id);
      if (result.success) {
        setMessage({ severity: 'success', text: `Benutzer ${user.email} wurde gelöscht.` });
        setRefreshTrigger(prev => prev + 1);
      } else {
        setMessage({ severity: 'error', text: result.message || 'Benutzer konnte nicht gelöscht werden.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Benutzer konnte nicht gelöscht werden.' });
    } finally {
      setDeletingUserId(null);
    }
  };

  const buildWhatsAppMessage = (user: AdminUser): string => {
    const alias = String(user.alias || '').trim();
    const name = alias || user.email;
    const closings = [
      'Viele Gruesse, dein SE-Team.',
      'Viele Gruesse, deine Firma SE.',
      'Liebe Gruesse aus dem SE-Team.',
      'Viele Gruesse und bis gleich in der App, dein SE-Team.'
    ];
    const coolLines = [
      'P.S. Wissen rostet ohne Bewegung - bring es wieder zum Glanzen.',
      'P.S. Dein Team ist auf dein Wissen angewiesen!',
      'P.S. Ein kurzer Login, grosser Impact.',
      'P.S. Jeder Punkt zaehlt - komm zurueck und sammle sie.'
    ];
    const totalScore = Object.keys(user)
      .filter(key => key.startsWith('quiz_'))
      .reduce((sum, key) => sum + parseScore(user[key as keyof AdminUser] as number | string | undefined), 0);
    const includeScore = totalScore > 0 && Math.random() < 0.5;
    const closing = closings[Math.floor(Math.random() * closings.length)];
    const coolLine = coolLines[Math.floor(Math.random() * coolLines.length)];
    const scoreLine = includeScore ? `Dein aktueller Gesamt-Score: ${totalScore}.` : '';

    return [
      `Hey ${name}, du warst schon lange nicht mehr in der App.`,
      'Komm und logg dich ein unter https://app.portal.se-cloud.de/.',
      `Dein Username ist: ${user.email}.`,
      scoreLine,
      'Wenn du diese Benachrichtigung nicht mehr moechtest, logg dich ein und deaktiviere deine Nummer.',
      closing,
      coolLine
    ].filter(Boolean).join(' ');
  };

  const handleOpenWhatsAppDialog = (user: AdminUser) => {
    setWaDialogUser(user);
    setWaMessage(buildWhatsAppMessage(user));
  };

  const handleSendWhatsApp = async () => {
    if (!token || !waDialogUser) return;
    const trimmed = waMessage.trim();
    if (!trimmed) {
      setMessage({ severity: 'error', text: 'Die WhatsApp-Nachricht darf nicht leer sein.' });
      return;
    }
    setMessage(null);
    setWaSending(true);
    try {
      const result = await authService.adminSendWhatsAppMessage(token, waDialogUser.id, trimmed);
      if (result.success) {
        setMessage({
          severity: 'success',
          text: `WhatsApp-Nachricht wurde an ${waDialogUser.email} gesendet.`
        });
        setWaDialogUser(null);
      } else {
        setMessage({ severity: 'error', text: result.message || 'WhatsApp-Nachricht konnte nicht gesendet werden.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'WhatsApp-Nachricht konnte nicht gesendet werden.' });
    } finally {
      setWaSending(false);
    }
  };

  const handleCreateUser = async () => {
    if (!token) return;
    const email = newUserEmail.trim();
    const alias = newUserAlias.trim();
    if (!email || !alias) {
      setMessage({ severity: 'error', text: 'E-Mail und Alias sind erforderlich.' });
      return;
    }
    setMessage(null);
    setCreatingUser(true);
    try {
      const result = await authService.adminCreateUser(token, {
        email,
        alias,
        roles: newUserRoles,
        active: newUserActive,
        event: newUserEvent.trim() || null,
        weeklyGoalNotifyEnabled: newUserWeeklyGoalNotifyEnabled
      });
      if (result.success) {
        setMessage({ severity: 'success', text: 'Benutzer wurde angelegt (ohne Passwort).' });
        setNewUserEmail('');
        setNewUserAlias('');
        setNewUserRoles(['user']);
        setNewUserActive(false);
        setNewUserEvent('');
        setNewUserWeeklyGoalNotifyEnabled(true);
        setRefreshTrigger(prev => prev + 1);
      } else {
        setMessage({ severity: 'error', text: result.message || 'Benutzer konnte nicht angelegt werden.' });
      }
    } catch (e: any) {
      setMessage({ severity: 'error', text: e?.message || 'Benutzer konnte nicht angelegt werden.' });
    } finally {
      setCreatingUser(false);
    }
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccessPage) {
    return (
      <UnitPageShell title="Zugriff verweigert">
        <Container maxWidth={false} disableGutters sx={{ py: 6, px: { xs: 2, md: 4 } }}>
          <Alert severity="error">Keine Berechtigung fuer diese Seite.</Alert>
        </Container>
      </UnitPageShell>
    );
  }

  const getEditValue = (user: AdminUser, field: keyof AdminUser): string => {
    const stored = edits[user.id]?.[field];
    if (stored !== undefined) {
      return String(stored ?? '');
    }
    const fallback = user[field];
    return fallback === null || fallback === undefined ? '' : String(fallback);
  };

  const getEditRoles = (user: AdminUser): string[] => {
    const stored = edits[user.id]?.roles;
    if (stored !== undefined) {
      return normalizeRoles(stored);
    }
    return normalizeRoles(user.roles ?? user.role ?? 'user');
  };

  const getEditWeeklyGoalNotify = (user: AdminUser): boolean => {
    const stored = edits[user.id]?.weekly_goal_notify_enabled;
    if (stored !== undefined) {
      return Boolean(stored);
    }
    return Boolean(user.weekly_goal_notify_enabled ?? true);
  };

  return (
    <UnitPageShell
      title="Admin: Benutzerverwaltung"
      actions={(
        <>
          {canManageUsers && (
            <TextField
              size="small"
              placeholder="Suchen (Alias oder E-Mail)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 260 }, maxWidth: 320 }}
            />
          )}
          {canManageUpdates && (
            <Button
              color="inherit"
              component={RouterLink}
              to="/admin/updates"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Updates verwalten
            </Button>
          )}
          {canManageUsers && (
            <Button
              color="inherit"
              startIcon={<RefreshIcon />}
              onClick={() => setRefreshTrigger(prev => prev + 1)}
              disabled={loading}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Benutzerliste aktualisieren
            </Button>
          )}
          {canViewRolesLegend && (
            <Button
              color="inherit"
              component={RouterLink}
              to="/admin/roles"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Rollen
            </Button>
          )}
        </>
      )}
      columns={{ xs: '1fr' }}
    >
      <Container maxWidth={false} disableGutters sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
        <Paper sx={{ p: { xs: 2, md: 4 } }} elevation={1}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Verwaltung aller Benutzer
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Die Oberfläche orientiert sich an der Android-Navigation: pro Benutzer gibt es eine Zeile mit Alias, E-Mail,
            Rolle, Status und einem Hinweisfeld. Änderungen werden nach dem Speichern serverseitig übernommen,
            damit der Android-Client und das Backend synchron bleiben.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {message && (
            <Alert severity={message.severity} sx={{ mt: 2 }}>
              {message.text}
            </Alert>
          )}

          {canManageUsers && (
            <Accordion elevation={0} disableGutters sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Benutzer anlegen (ohne Passwort)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    label="E-Mail"
                    value={newUserEmail}
                    onChange={e => setNewUserEmail(e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Alias"
                    value={newUserAlias}
                    onChange={e => setNewUserAlias(e.target.value)}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="new-role-label">Rollen</InputLabel>
                    <Select
                      labelId="new-role-label"
                      label="Rollen"
                      multiple
                      value={newUserRoles}
                      onChange={e => setNewUserRoles(e.target.value as string[])}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip key={value} label={roleLabel(value)} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {ROLE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 2, alignItems: { md: 'center' } }}>
                  <TextField
                    fullWidth
                    label="WhatsApp (event)"
                    helperText="Optional"
                    value={newUserEvent}
                    onChange={e => setNewUserEvent(e.target.value)}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={newUserActive}
                        onChange={e => setNewUserActive(e.target.checked)}
                      />
                    }
                    label={newUserActive ? 'Aktiv' : 'Deaktiviert'}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={newUserWeeklyGoalNotifyEnabled}
                        onChange={e => setNewUserWeeklyGoalNotifyEnabled(e.target.checked)}
                      />
                    }
                    label={newUserWeeklyGoalNotifyEnabled ? 'Wochenziel-Erinnerung aktiv' : 'Wochenziel-Erinnerung aus'}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCreateUser}
                    disabled={creatingUser}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {creatingUser ? 'Lege an ...' : 'Benutzer anlegen'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {canManageTeams && (
            <Accordion elevation={0} disableGutters sx={{ mt: 2, mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Teams verwalten
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Berechtigte Nutzer koennen Teamnamen anpassen. Diese Namen erscheinen im Highscore-Filter.
                </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' }, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Neues Team"
                  placeholder="z.B. Team CRD"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  disabled={teamsLoading || teamsSaving || creatingTeam}
                />
                <Button
                  variant="contained"
                  onClick={handleCreateTeam}
                  disabled={teamsLoading || teamsSaving || creatingTeam}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {creatingTeam ? 'Lege an ...' : 'Team anlegen'}
                </Button>
              </Box>
              {canViewRolesLegend && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to="/admin/roles"
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Rollen verwalten
                  </Button>
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' } }}>
                {teams.map((team) => (
                  <Box key={team.id} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      fullWidth
                      label={`Team ${team.id}`}
                      value={teamEdits[team.id] ?? team.name}
                      onChange={(e) => setTeamEdits((prev) => ({ ...prev, [team.id]: e.target.value }))}
                      disabled={teamsLoading || teamsSaving}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={teamActiveEdits[team.id] ?? true}
                          onChange={(e) => setTeamActiveEdits((prev) => ({ ...prev, [team.id]: e.target.checked }))}
                          disabled={teamsLoading || teamsSaving}
                        />
                      }
                      label="Im Highscore auswählbar"
                    />
                  </Box>
                ))}
                <Button
                  variant="contained"
                  onClick={handleSaveTeams}
                  disabled={teamsLoading || teamsSaving || teams.length === 0}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {teamsSaving ? 'Speichere ...' : 'Teams speichern'}
                </Button>
              </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {canManageUsers && (
            <Accordion elevation={0} disableGutters sx={{ mt: 2, mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Rollen-Ziele (Mindestpunkte)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Definiere pro Rolle eine Mindestpunktzahl. Nutzer mit aktivierter Wochenziel-Erinnerung erhalten freitags um 07:00 Uhr
                  eine WhatsApp, falls sie unter dem Rollenziel liegen.
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                    gap: 2
                  }}
                >
                  {availableGoalRoles.map((roleValue) => (
                    <TextField
                      key={roleValue}
                      label={`${roleLabel(roleValue)} (Punkte)`}
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      helperText="Leer lassen = kein Ziel"
                      value={rolePointGoalEdits[roleValue] ?? ''}
                      onChange={(e) => handleRoleGoalInputChange(roleValue, e.target.value)}
                      disabled={roleGoalsLoading || roleGoalsSaving || runningWeeklyRoleGoals}
                    />
                  ))}
                </Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveRolePointGoals}
                    disabled={roleGoalsLoading || roleGoalsSaving}
                  >
                    {roleGoalsSaving ? 'Speichere Ziele ...' : 'Ziele speichern'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleRunWeeklyRoleGoalNotifications}
                    disabled={runningWeeklyRoleGoals || roleGoalsLoading}
                  >
                    {runningWeeklyRoleGoals ? 'Sende Erinnerungen ...' : 'Test: Erinnerungen jetzt senden'}
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {!canManageUsers ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Keine Berechtigung fuer die Benutzerverwaltung.
            </Alert>
          ) : loading ? (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ mt: 4 }}>
              <Typography variant="body1">Keine Benutzer gefunden.</Typography>
            </Box>
          ) : (
            <Stack spacing={2} mt={2}>
              {users
                .filter(u => {
                  const q = searchQuery.trim().toLowerCase();
                  if (!q) return true;
                  const alias = String(u.alias || '').toLowerCase();
                  const email = String(u.email || '').toLowerCase();
                  return alias.includes(q) || email.includes(q);
                })
                .sort((a, b) => {
                  const rankRoles = (roles: string[]) => {
                    if (roles.includes('admin')) return 0;
                    if (roles.includes('light_admin')) return 1;
                    if (roles.includes('user_admin')) return 2;
                    return 3;
                  };
                  const ra = rankRoles(normalizeRoles(a.roles ?? a.role ?? 'user'));
                  const rb = rankRoles(normalizeRoles(b.roles ?? b.role ?? 'user'));
                  if (ra !== rb) return ra - rb;
                  const ea = String(a.email || '').toLowerCase();
                  const eb = String(b.email || '').toLowerCase();
                  return ea.localeCompare(eb);
                })
                .map(user => (
                  <Accordion key={user.id} elevation={2} expanded={expanded === user.id} onChange={handleAccordionChange(user.id)}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="subtitle1" fontWeight="bold">{user.alias || user.email}</Typography>
                        <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {normalizeRoles(user.roles ?? user.role ?? 'user').map((roleValue) => (
                              <Chip key={roleValue} label={roleLabel(roleValue)} size="small" />
                            ))}
                          </Box>
                          <Chip label={user.active ? 'Aktiv' : 'Gesperrt'} color={user.active ? 'success' : 'warning'} />
                          {user.deleted && <Chip label="Gelöscht" color="error" />}
                        </Stack>
                        <Divider />
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                          <TextField
                            fullWidth
                            label="E-Mail"
                            value={getEditValue(user, 'email')}
                            onChange={e => handleFieldChange(user.id, 'email', e.target.value)}
                            disabled={busyUsers[user.id]}
                          />
                          <TextField
                            fullWidth
                            label="Alias"
                            value={getEditValue(user, 'alias')}
                            onChange={e => handleFieldChange(user.id, 'alias', e.target.value)}
                            disabled={busyUsers[user.id]}
                          />
                          <FormControl fullWidth>
                            <InputLabel id={`role-label-${user.id}`}>Rollen</InputLabel>
                            <Select
                              labelId={`role-label-${user.id}`}
                              label="Rollen"
                              multiple
                              value={getEditRoles(user)}
                              onChange={e => handleFieldChange(user.id, 'roles', e.target.value as string[])}
                              disabled={busyUsers[user.id]}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(selected as string[]).map((value) => (
                                    <Chip key={value} label={roleLabel(value)} size="small" />
                                  ))}
                                </Box>
                              )}
                            >
                              {ROLE_OPTIONS.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl fullWidth>
                            <InputLabel id={`team-label-${user.id}`}>Team</InputLabel>
                            <Select
                              labelId={`team-label-${user.id}`}
                              label="Team"
                              value={getEditValue(user, 'team_id') || ''}
                              onChange={e => handleFieldChange(user.id, 'team_id', e.target.value)}
                              disabled={busyUsers[user.id]}
                            >
                              <MenuItem value="">Kein Team</MenuItem>
                              {teams.map(team => (
                                <MenuItem key={team.id} value={String(team.id)}>
                                  {teamEdits[team.id] ?? team.name}
                                  {(team.is_active === 0 || team.is_active === false) ? ' (inaktiv)' : ''}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            fullWidth
                            label="WhatsApp (event)"
                            helperText="Nur für Benachrichtigungen nutzen"
                            value={getEditValue(user, 'event')}
                            onChange={e => handleFieldChange(user.id, 'event', e.target.value)}
                            disabled={busyUsers[user.id]}
                          />
                        </Box>
                        <TextField
                          fullWidth
                          label="Info / letzte Aktivität"
                          multiline
                          minRows={2}
                          value={getEditValue(user, 'info')}
                          onChange={e => handleFieldChange(user.id, 'info', e.target.value)}
                          disabled={busyUsers[user.id]}
                        />

                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={Boolean(user.active)}
                                  onChange={() => handleToggleActive(user)}
                                  disabled={busyUsers[user.id]}
                                />
                              }
                              label={user.active ? 'Aktiv' : 'Deaktiviert'}
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={getEditWeeklyGoalNotify(user)}
                                  onChange={(e) => handleFieldChange(user.id, 'weekly_goal_notify_enabled', e.target.checked)}
                                  disabled={busyUsers[user.id]}
                                />
                              }
                              label={getEditWeeklyGoalNotify(user) ? 'Wochenziel-Erinnerung aktiv' : 'Wochenziel-Erinnerung aus'}
                            />
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              onClick={() => handleSave(user)}
                              disabled={busyUsers[user.id]}
                            >
                              Änderungen speichern
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => handleOpenWhatsAppDialog(user)}
                              disabled={busyUsers[user.id] || !user.event}
                            >
                              WhatsApp erinnern
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => handleDelete(user)}
                              disabled={busyUsers[user.id] || deletingUserId === user.id}
                            >
                              {deletingUserId === user.id ? 'Lösche …' : 'Benutzer löschen'}
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
            </Stack>
          )}
        </Paper>
      </Container>
      <Dialog open={Boolean(waDialogUser)} onClose={() => setWaDialogUser(null)} fullWidth maxWidth="sm">
        <DialogTitle>WhatsApp-Nachricht senden</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Empfaenger: {waDialogUser?.alias || waDialogUser?.email}
              {waDialogUser?.event ? ` (Nummer: ${waDialogUser.event})` : ''}
            </Typography>
            <TextField
              label="Nachricht"
              multiline
              minRows={4}
              value={waMessage}
              onChange={e => setWaMessage(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaDialogUser(null)} disabled={waSending}>
            Abbrechen
          </Button>
          <Button variant="contained" onClick={handleSendWhatsApp} disabled={waSending}>
            {waSending ? 'Sende ...' : 'Nachricht senden'}
          </Button>
        </DialogActions>
      </Dialog>
    </UnitPageShell>
  );
};

export default AdminPage;
